import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { DateParsersService } from 'rev-shared/date/DateParsers.Service';
import { IDateRange } from 'rev-shared/ui/datePicker/DateRangePicker.Component';
import { IGetInfiniteScrollRows } from 'rev-shared/ui/dataGrid/infiniteScroll/IGetInfiniteScrollRows';
import { IVbUiInfiniteScrollGridDataSourceResult } from 'rev-shared/ui/dataGrid/infiniteScroll/IVbUiInfiniteScrollGridDataSourceResult';
import { formatIsoDate } from 'rev-shared/date/DateFormatters';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';

import { GridType } from './Constant';
import { IAnalyticsSettings } from './IAnalyticsSettings';

interface IContext {
	startDate: string;
	endDate: string;
}

@Injectable({
	providedIn: 'root'
})
export class AnalyticsService {
	private realtimeKpiUpdateIntervalInSecs: number;
	private realtimeTableUpdateIntervalInSecs: number;
	private dateFilterSubject$: BehaviorSubject<IDateRange> = new BehaviorSubject<IDateRange>(undefined);
	private gridFetchEndpoint = {
		[GridType.EVENTS]: accountId => `/analytics/account/${accountId}/events`,
		[GridType.SESSIONS]: accountId => `/analytics/account/${accountId}/videos/user-sessions`,
		[GridType.LIVE_EVENTS]: accountId => `/analytics/account/${accountId}/events/real-time/performance`
	};

	public dateFilter$: Observable<IDateRange> = this.dateFilterSubject$.asObservable();
	public dataFetchContext$: Observable<IContext> = this.dateFilterSubject$
		.pipe(
			map(dateFilter => {
				const { range } = dateFilter || {};
				const [startDate, endDate] = range?.map(formatIsoDate) || [];

				return { startDate, endDate };
			})
		);

	constructor(
		private dateParsers: DateParsersService,
		private http: HttpClient
	) {}

	public setDateFilter(dateFilter: IDateRange): void {
		this.dateFilterSubject$.next(dateFilter);
	}


	public getRows: IGetInfiniteScrollRows = params => {
		return this.fetch(
			params.context.accountId,
			params.context.type,
			{
				size: params.pageSize,
				search: params.query,
				fields: params.filterFields,
				scrollId: params.scrollId,
				previousScrollId: params.previousScrollId,
				sortDirection: params.isSortAscending ? 'asc' : 'desc',
				sortBy: params.sortField,
				startDate: params.dataFetchContext?.startDate,
				endDate: params.dataFetchContext?.endDate
			}
		).then<IVbUiInfiniteScrollGridDataSourceResult>(result => ({
			data: result[params.context.type],
			scrollId: result.scrollId,
			totalRows: result.total
		}));
	};

	private fetch(accountId: string, type: GridType, params: any): Promise<any> {
		return lastValueFrom(this.http.post<any>(this.gridFetchEndpoint[type](accountId), params)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					console.error(error);
					if (error.status === 404) {
						throw error;
					}
					//ensuring ag-grid does not fail..
					return of({
						[type]: [],
						scrollId: undefined,
						total: 0
					});
				})
			));
	}

	public getSettings(): Promise<IAnalyticsSettings> {
		return lastValueFrom(this.http.get<IAnalyticsSettings>(`/network/analytics-settings`))
			.then(result => {
				[
					'minHeartbeat',
					'videoHeartbeat',
					'webcastHeartbeat',
					'webcastRealtimeKpiUpdateInterval',
					'webcastRealtimeTableUpdateInterval',
					'webcastRealtimeAggregationInterval',
					'webcastRealtimeAggregationTimeout'
				].forEach(field => result[field] = this.dateParsers.parseTimespanToSecs(result[field]));

				return result;
			})
			.then(result => {
				this.realtimeKpiUpdateIntervalInSecs = result.webcastRealtimeKpiUpdateInterval;
				this.realtimeTableUpdateIntervalInSecs = result.webcastRealtimeTableUpdateInterval;
				return result;
			});
	}

	public get kpiUpdateIntervalInSecs(): number {
		return this.realtimeKpiUpdateIntervalInSecs;
	}

	public get tableUpdateIntervalInSecs(): number {
		return this.realtimeTableUpdateIntervalInSecs;
	}

	public clearEventScrollId = ({ webcastId, runNumber }: any, scrollId: string) => {
		lastValueFrom(this.http.delete(`/analytics/events/${webcastId}/${runNumber}/scroll`, { params: { scrollId } }));
	};

	public clearVideoScrollId = ({ videoId }: any, scrollId: string) => {
		lastValueFrom(this.http.delete(`/analytics/videos/${videoId}/scroll`, { params: { scrollId } }));
	};

}
